import { useCallback, useEffect, useMemo, useState } from 'react';
import { FoodCategoryPortion } from '@/types/food-category';
import { RecipeServingSize } from '@/types/custom-recipe';
import {
  getSuggestedPortionsForDishTypes,
  isSuggestedRecipePortionId,
  mergePortionOptions,
  normalizeRecipePortionId,
  portionOptionValue,
  slugifyPortionName,
} from '@/constants/recipe-serving-options';

const GRAM_SERVING_PATTERN = /^(g|gram|grams)$/i;

function isGramServing(value: unknown): boolean {
  return GRAM_SERVING_PATTERN.test(String(value ?? '').trim());
}

function roundPositiveNumber(value: unknown): number | null {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return null;
  return Math.round(numeric * 100) / 100;
}

function humanizePortionId(value: string): string {
  const normalized = String(value ?? '').trim();
  if (!normalized) return '';
  return normalized
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function resolvePortionGrams(portion: FoodCategoryPortion): number | null {
  const servingIdRaw = portion.servingId ?? portion.id ?? portion.name;
  const grams = roundPositiveNumber(portion.gramsOverride ?? portion.weightG ?? portion.suggestedWeightG);
  if (grams != null) return grams;
  if (isGramServing(servingIdRaw)) return 1;
  return null;
}

function resolvePortionMl(portion: FoodCategoryPortion): number | null {
  return roundPositiveNumber(portion.volumeMl);
}

function roundDensity(value: unknown): number | null {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return null;
  return Math.round(numeric * 10000) / 10000;
}

function resolveDensityFromPortions(portions: FoodCategoryPortion[]): number | null {
  for (const portion of portions) {
    const density = roundDensity(portion.densityGPerMl);
    if (density != null) return density;
  }
  return null;
}

function applyDensityToPortions(
  portions: FoodCategoryPortion[],
  densityGPerMl: number | null,
): FoodCategoryPortion[] {
  if (densityGPerMl == null) {
    return portions.map((portion) => ({
      ...portion,
      volumeMl: undefined,
      densityGPerMl: undefined,
    }));
  }

  return portions.map((portion) => {
    const ml = resolvePortionMl(portion);
    const grams = ml != null
      ? Math.round(ml * densityGPerMl * 100) / 100
      : resolvePortionGrams(portion);

    return {
      ...portion,
      ...(grams != null ? { gramsOverride: grams, weightG: grams } : {}),
      ...(grams != null ? { volumeMl: Math.round((grams / densityGPerMl) * 100) / 100 } : {}),
      densityGPerMl,
    };
  });
}

type HydrateOptions = {
  servingSizes?: RecipeServingSize[] | null;
  defaultServingId?: string | null;
  defaultServingGrams?: number | null;
  dishTypesOverride?: string[];
};

export function useRecipePortionSizes(dishTypes: string[]) {
  const suggestedPortions = useMemo(
    () => getSuggestedPortionsForDishTypes(dishTypes),
    [dishTypes]
  );
  const [removedSuggestedPortionIds, setRemovedSuggestedPortionIds] = useState<Set<string>>(() => new Set());
  const activeSuggestedPortions = useMemo(
    () => suggestedPortions.filter((portion, index) => (
      !removedSuggestedPortionIds.has(normalizeRecipePortionId(portionOptionValue(portion, index)))
    )),
    [removedSuggestedPortionIds, suggestedPortions],
  );

  const [portionSizes, setPortionSizes] = useState<FoodCategoryPortion[]>(() => activeSuggestedPortions);
  const [portionSizeId, setPortionSizeId] = useState<string>('');
  const [defaultServingGrams, setDefaultServingGrams] = useState<number | ''>('');
  const [portionDensityGPerMl, setPortionDensityGPerMlState] = useState<number | ''>('');
  const [customPortionName, setCustomPortionName] = useState('');
  const [customPortionGrams, setCustomPortionGrams] = useState<number | ''>('');
  const [customPortionMl, setCustomPortionMlState] = useState<number | ''>('');

  const syncPortionsWithDensity = useCallback((nextPortions: FoodCategoryPortion[]) => {
    const density = typeof portionDensityGPerMl === 'number'
      ? portionDensityGPerMl
      : resolveDensityFromPortions(nextPortions);
    return applyDensityToPortions(nextPortions, density);
  }, [portionDensityGPerMl]);

  const setPortionDensityGPerMl = useCallback((value: number | '') => {
    const density = value === '' ? null : roundDensity(value);
    setPortionDensityGPerMlState(density ?? '');
    setPortionSizes((prev) => applyDensityToPortions(prev, density));
    if (density == null) {
      setCustomPortionMlState('');
      return;
    }
    setCustomPortionGrams((grams) => {
      if (typeof grams === 'number' && grams > 0) {
        setCustomPortionMlState(Math.round((grams / density) * 100) / 100);
      }
      return grams;
    });
  }, []);

  const setCustomPortionGramsWithDensity = useCallback((value: number | '') => {
    setCustomPortionGrams(value);
    const density = typeof portionDensityGPerMl === 'number' ? portionDensityGPerMl : null;
    if (density != null && typeof value === 'number' && value > 0) {
      setCustomPortionMlState(Math.round((value / density) * 100) / 100);
    }
  }, [portionDensityGPerMl]);

  const setCustomPortionMl = useCallback((value: number | '') => {
    const density = typeof portionDensityGPerMl === 'number' ? portionDensityGPerMl : null;
    if (density == null) return;
    setCustomPortionMlState(value);
    if (typeof value === 'number' && value > 0) {
      setCustomPortionGrams(Math.round(value * density * 100) / 100);
    }
  }, [portionDensityGPerMl]);

  const reconcileSuggestedPortions = useCallback((previousPortions: FoodCategoryPortion[]) => {
    const activeSuggestionIds = new Set(
      activeSuggestedPortions.map((portion, index) => normalizeRecipePortionId(portionOptionValue(portion, index)))
    );

    const relevantExistingPortions = previousPortions.filter((portion, index) => {
      const optionId = normalizeRecipePortionId(portionOptionValue(portion, index));
      if (!optionId) return false;
      return activeSuggestionIds.has(optionId) || !isSuggestedRecipePortionId(optionId);
    });

    return syncPortionsWithDensity(mergePortionOptions(activeSuggestedPortions, relevantExistingPortions));
  }, [activeSuggestedPortions, syncPortionsWithDensity]);

  useEffect(() => {
    setPortionSizes((prev) => reconcileSuggestedPortions(prev));
  }, [reconcileSuggestedPortions]);

  useEffect(() => {
    const hasSelectedPortion = portionSizeId
      ? portionSizes.some((portion, index) => (
          normalizeRecipePortionId(portionOptionValue(portion, index)) === normalizeRecipePortionId(portionSizeId)
        ))
      : false;

    if (hasSelectedPortion) return;

    const nextDefaultIndex = portionSizes.findIndex((portion) => portion.isDefault);
    const fallbackIndex = nextDefaultIndex >= 0 ? nextDefaultIndex : 0;
    const nextDefault = portionSizes[fallbackIndex];

    if (!nextDefault) {
      if (portionSizeId) setPortionSizeId('');
      setDefaultServingGrams('');
      return;
    }

    setPortionSizeId(portionOptionValue(nextDefault, fallbackIndex));
  }, [portionSizeId, portionSizes]);

  useEffect(() => {
    if (!portionSizeId || portionSizes.length === 0) return;
    const selectedPortion = portionSizes.find((portion, index) => (
      normalizeRecipePortionId(portionOptionValue(portion, index)) === normalizeRecipePortionId(portionSizeId)
    ));
    if (!selectedPortion) return;
    const grams = resolvePortionGrams(selectedPortion);
    if (grams == null) return;
    setDefaultServingGrams((current) => {
      if (typeof current === 'number' && Math.abs(current - grams) < 0.01) {
        return current;
      }
      return grams;
    });
  }, [portionSizeId, portionSizes]);

  const setPortionWeight = useCallback((servingIdRaw: string, value: number | '') => {
    const servingId = normalizeRecipePortionId(servingIdRaw);
    if (!servingId) return;

    const nextPortions = portionSizes.map((portion, index) => {
      const optionId = normalizeRecipePortionId(portionOptionValue(portion, index));
      if (optionId !== servingId) return portion;

      if (isGramServing(servingId)) {
        return {
          ...portion,
          gramsOverride: 1,
          weightG: 1,
        };
      }

      if (value === '' || !Number.isFinite(value) || value <= 0) {
        return {
          ...portion,
          gramsOverride: undefined,
          weightG: undefined,
          volumeMl: undefined,
        };
      }

      const rounded = Math.round(value * 100) / 100;
      const density = typeof portionDensityGPerMl === 'number' ? portionDensityGPerMl : null;
      return {
        ...portion,
        gramsOverride: rounded,
        weightG: rounded,
        ...(density != null ? { volumeMl: Math.round((rounded / density) * 100) / 100, densityGPerMl: density } : {}),
      };
    });

    setPortionSizes(nextPortions);
  }, [portionDensityGPerMl, portionSizes]);

  const setPortionVolume = useCallback((servingIdRaw: string, value: number | '') => {
    const servingId = normalizeRecipePortionId(servingIdRaw);
    if (!servingId) return;

    const density = typeof portionDensityGPerMl === 'number' ? portionDensityGPerMl : null;
    if (density == null) return;

    if (value === '' || !Number.isFinite(value) || value <= 0) {
      setPortionSizes((prev) => prev.map((portion, index) => (
        normalizeRecipePortionId(portionOptionValue(portion, index)) === servingId
          ? { ...portion, volumeMl: undefined }
          : portion
      )));
      return;
    }

    const rounded = Math.round(value * 100) / 100;
    const grams = Math.round(rounded * density * 100) / 100;
    const nextPortions = portionSizes.map((portion, index) => {
      const optionId = normalizeRecipePortionId(portionOptionValue(portion, index));
      if (optionId !== servingId) return portion;
      return {
        ...portion,
        volumeMl: rounded,
        gramsOverride: grams,
        weightG: grams,
        densityGPerMl: density,
      };
    });

    setPortionSizes(nextPortions);
  }, [portionDensityGPerMl, portionSizes]);

  const setPortionQuantity = useCallback((servingIdRaw: string, value: number | '') => {
    const servingId = normalizeRecipePortionId(servingIdRaw);
    if (!servingId) return;

    setPortionSizes((prev) => prev.map((portion, index) => {
      const optionId = normalizeRecipePortionId(portionOptionValue(portion, index));
      if (optionId !== servingId) return portion;

      const quantity = roundPositiveNumber(value);
      return {
        ...portion,
        quantity: quantity ?? undefined,
      };
    }));
  }, []);

  const removePortion = useCallback((servingIdRaw: string) => {
    const servingId = normalizeRecipePortionId(servingIdRaw);
    if (!servingId) return;

    setPortionSizes((prev) => {
      if (prev.length <= 1) {
        return prev;
      }
      if (isSuggestedRecipePortionId(servingId)) {
        setRemovedSuggestedPortionIds((current) => {
          const next = new Set(current);
          next.add(servingId);
          return next;
        });
      }
      return prev.filter((portion, index) => (
        normalizeRecipePortionId(portionOptionValue(portion, index)) !== servingId
      ));
    });

    if (normalizeRecipePortionId(portionSizeId) === servingId) {
      setPortionSizeId('');
      setDefaultServingGrams('');
    }
  }, [portionSizeId]);

  const addCustomPortion = useCallback(() => {
    const trimmedName = customPortionName.trim();
    const grams = roundPositiveNumber(customPortionGrams);
    if (!trimmedName || grams == null) {
      return { success: false as const };
    }

    const baseId = slugifyPortionName(trimmedName);
    if (!baseId) {
      return { success: false as const };
    }

    const existingIds = new Set(
      portionSizes.map((portion, index) => portionOptionValue(portion, index))
    );

    let servingId = baseId;
    let suffix = 2;
    while (existingIds.has(servingId)) {
      servingId = `${baseId}_${suffix}`;
      suffix += 1;
    }

    const customPortion: FoodCategoryPortion = {
      servingId,
      id: servingId,
      name: trimmedName,
      gramsOverride: grams,
      weightG: grams,
      quantity: 1,
      ...(typeof portionDensityGPerMl === 'number' ? { volumeMl: Math.round((grams / portionDensityGPerMl) * 100) / 100, densityGPerMl: portionDensityGPerMl } : {}),
      suggestedWeightG: grams,
      isCustom: true,
      sortOrder: portionSizes.length,
    };

    const nextPortions = [...portionSizes, customPortion];
    setPortionSizes(nextPortions);
    setPortionSizeId(servingId);
    setDefaultServingGrams(grams);
    setCustomPortionName('');
    setCustomPortionGrams('');
    setCustomPortionMlState('');
    return { success: true as const, servingId };
  }, [customPortionGrams, customPortionName, portionDensityGPerMl, portionSizes]);

  const hydrateFromServingSizes = useCallback((options: HydrateOptions) => {
    const incomingSizes = Array.isArray(options.servingSizes) ? options.servingSizes : [];
    const mappedPortions: FoodCategoryPortion[] = incomingSizes
      .map((entry) => {
        const servingId = normalizeRecipePortionId(entry?.servingId);
        const grams = roundPositiveNumber(entry?.grams);
        const density = roundDensity(entry?.densityGPerMl);
        const ml = density != null ? roundPositiveNumber(entry?.ml) : null;
        if (!servingId || grams == null) return null;
        return {
          servingId,
          id: servingId,
          name: entry?.label ?? servingId,
          gramsOverride: grams,
          weightG: grams,
          quantity: roundPositiveNumber(entry?.quantity) ?? undefined,
          ...(ml != null ? { volumeMl: ml } : {}),
          ...(density != null ? { densityGPerMl: density } : {}),
          suggestedWeightG: grams,
          isDefault: entry?.isDefault === true,
          isCustom: !isSuggestedRecipePortionId(servingId),
        } as FoodCategoryPortion;
      })
      .filter((entry): entry is FoodCategoryPortion => entry !== null);

    const fallbackDefaultServingId = normalizeRecipePortionId(options.defaultServingId);
    const fallbackDefaultServingGrams = roundPositiveNumber(options.defaultServingGrams);
    if (
      fallbackDefaultServingId
      && fallbackDefaultServingGrams != null
      && !mappedPortions.some((portion, index) => (
        normalizeRecipePortionId(portionOptionValue(portion, index)) === fallbackDefaultServingId
      ))
    ) {
      mappedPortions.push({
        servingId: fallbackDefaultServingId,
        id: fallbackDefaultServingId,
        name: humanizePortionId(fallbackDefaultServingId),
        gramsOverride: fallbackDefaultServingGrams,
        weightG: fallbackDefaultServingGrams,
        suggestedWeightG: fallbackDefaultServingGrams,
        isDefault: true,
        isCustom: !isSuggestedRecipePortionId(fallbackDefaultServingId),
      });
    }

    const effectiveDishTypes = options.dishTypesOverride ?? dishTypes;
    const nextSuggestions = getSuggestedPortionsForDishTypes(effectiveDishTypes);
    setRemovedSuggestedPortionIds(new Set());
    const merged = mergePortionOptions(nextSuggestions, mappedPortions);
    const nextDensity = resolveDensityFromPortions(mappedPortions);
    setPortionDensityGPerMlState(nextDensity ?? '');
    const normalizedPortions = applyDensityToPortions(merged, nextDensity);
    setPortionSizes(normalizedPortions);
    setPortionSizeId(options.defaultServingId ?? incomingSizes.find((entry) => entry?.isDefault)?.servingId ?? normalizedPortions[0]?.servingId ?? '');

    const grams = roundPositiveNumber(options.defaultServingGrams);
    if (grams != null) {
      setDefaultServingGrams(grams);
      return;
    }

    const defaultPortion = normalizedPortions.find((portion, index) => (
      normalizeRecipePortionId(portionOptionValue(portion, index)) === normalizeRecipePortionId(options.defaultServingId)
    )) ?? normalizedPortions[0];
    setDefaultServingGrams(defaultPortion ? (resolvePortionGrams(defaultPortion) ?? '') : '');
  }, [dishTypes]);

  return {
    portionSizes,
    portionSizeId,
    defaultServingGrams,
    portionDensityGPerMl,
    customPortionName,
    customPortionGrams,
    customPortionMl,
    setPortionSizes,
    setPortionSizeId,
    setDefaultServingGrams,
    setPortionDensityGPerMl,
    setCustomPortionName,
    setCustomPortionGrams: setCustomPortionGramsWithDensity,
    setCustomPortionMl,
    setPortionWeight,
    setPortionVolume,
    setPortionQuantity,
    removePortion,
    addCustomPortion,
    hydrateFromServingSizes,
    resolvePortionGrams,
    resolvePortionMl,
    getPortionOptionValue: portionOptionValue,
  };
}
